import React, { useState } from 'react';
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiDownload } from 'react-icons/fi';
import { exportToCSV } from '../../utils/exportUtils';
import Spinner from './Spinner';
import './DataTable.css';

const DataTable = ({
  columns,
  data,
  loading = false,
  searchable = true,
  searchPlaceholder = 'Search...',
  pagination = true,
  pageSize = 10,
  totalItems = 0,
  currentPage = 1,
  onPageChange,
  onSearch,
  searchValue = '',
  exportable = false,
  exportFilename = 'export',
  emptyMessage = 'No data found',
  actions,
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll
}) => {
  const [localSearch, setLocalSearch] = useState(searchValue);
  
  const totalPages = Math.ceil(totalItems / pageSize);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleExport = () => {
    if (data && data.length > 0) {
      const exportData = data.map(row => {
        const exportRow = {};
        columns.forEach(col => {
          if (col.accessor && col.exportable !== false) {
            exportRow[col.header] = row[col.accessor];
          }
        });
        return exportRow;
      });
      exportToCSV(exportData, `${exportFilename}.csv`);
    }
  };

  const handleSelectAll = (e) => {
    if (onSelectAll) {
      onSelectAll(e.target.checked);
    }
  };

  const renderCell = (column, row, index) => {
    if (column.render) {
      return column.render(row, index);
    }
    return row[column.accessor];
  };

  const renderPagination = () => {
    if (!pagination || totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="table-pagination">
        <div className="pagination-info">
          Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to{' '}
          {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
          >
            <FiChevronsLeft />
          </button>
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <FiChevronLeft />
          </button>
          {pages.map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <FiChevronRight />
          </button>
          <button
            className="pagination-btn"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            <FiChevronsRight />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="data-table-container">
      {(searchable || exportable || actions) && (
        <div className="table-toolbar">
          <div className="toolbar-left">
            {searchable && (
              <div className="table-search">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={localSearch}
                  onChange={handleSearchChange}
                />
              </div>
            )}
          </div>
          <div className="toolbar-right">
            {actions}
            {exportable && data && data.length > 0 && (
              <button className="export-btn" onClick={handleExport}>
                <FiDownload />
                <span>Export CSV</span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {selectable && (
                <th className="select-cell">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === data?.length && data?.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  style={{ width: column.width, textAlign: column.align || 'left' }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="loading-cell">
                  <Spinner size="medium" />
                </td>
              </tr>
            ) : !data || data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="empty-cell">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr key={row.id || rowIndex}>
                  {selectable && (
                    <td className="select-cell">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) => onSelectRow && onSelectRow(row.id, e.target.checked)}
                      />
                    </td>
                  )}
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      style={{ textAlign: column.align || 'left' }}
                    >
                      {renderCell(column, row, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
};

export default DataTable;
